import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { PillarCard } from "@/components/calv/PillarCard";
import { CTABlock } from "@/components/calv/CTABlock";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { Callout } from "@/components/calv/Callout";
import { StatsBlock } from "@/components/calv/StatsBlock";
import { DeltaHero } from "@/components/calv/DeltaHero";

export const Route = createFileRoute("/en/")({
  head: () => {
    const h = pageHead("home", "en");
    return { meta: h.meta, links: h.links };
  },
  component: HomePage,
});

const stats = [
  { number: "20–40", unit: "%", label: "ACoS improvement · 12 months" },
  { number: "10–200", unit: "K€", label: "monthly ad budgets" },
  { number: "~80", unit: "%", label: "e-commerce mandates" },
  { number: "+15–25", unit: "%", label: "tracking recovery · server-side" },
];

const proofPoints = [
  {
    k: "Mandate binding",
    v: "Clients for 5+ years",
    b: "Most of our mandates run for over five years. That's the economically most meaningful metric in an industry where agency switches often happen annually.",
  },
  {
    k: "In the market since",
    v: "2013",
    b: "Over ten years of operational experience in performance marketing — before PMax, before iOS 14.5, before AI-driven platforms. We know the disruptions of recent years because we've lived through them.",
  },
  {
    k: "Client profile",
    v: "around 80 % e-commerce",
    b: "We work with B2C and B2B companies across a range of industries — with a clear focus on e-commerce, complemented by SaaS clients.",
  },
];

const principles = [
  {
    n: "01",
    t: "System over disciplines",
    b: "We don't work in silos. SEO, SEA, CRO and tracking run on a shared data model — every action knows the others.",
  },
  {
    n: "02",
    t: "Data first, advice second",
    b: "No client project starts without an audit. If the data isn't sound, every strategy is guesswork — and we'll say so.",
  },
  {
    n: "03",
    t: "Run by the managing director",
    b: "No hand-off to juniors after the pitch. André Freier sits on every client account himself — by default, not as a premium upgrade.",
  },
  {
    n: "04",
    t: "Fees independent of ad spend",
    b: "We don't earn more when you spend more. That keeps the incentive on outcomes, not on media volume.",
  },
];

function HomePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Online performance · since 2013"
          compactMobile
          headline={
            <>
              Online marketing<br />
              that <em>adds up.</em>
            </>
          }
          lead="Online marketing only works when it's thought through economically. We run SEO, SEA, CRO, tracking, and AI as an integrated system — operated hands-on by the managing director, with measurable impact rather than reporting cosmetics."
          metaStrip={[
            "ROAS and ACoS targets met or exceeded",
            "€10–200k monthly ad budgets",
            "~80% e-commerce mandates",
          ]}
          secondaryMeta={["Germany-wide · DACH-wide"]}
          primaryCta={{
            eyebrow: "Free · about 30 minutes",
            label: "Request a strategy conversation",
            to: "/en/contact",
            search: { subject: "strategy-meeting" },
          }}
          secondaryCta={{
            label: "Or: test our methodical tools without signup",
            to: "/en/tools",
          }}
        />



        <Section
          number="02"
          kicker="By the numbers"
          headline={<>No staged cases.<br />Four figures from daily operations.</>}
          caption="Ranges from active mandates, not best-case showcases."
        >
          <RevealOnScroll>
            <StatsBlock stats={stats} />
          </RevealOnScroll>
          <Callout>More budget isn't a strategy. Control is.</Callout>
        </Section>

        <Section
          number="03"
          variant="tinted"
          kicker="What we do"
          headline={<>Services. <em>One shared data foundation.</em></>}
          caption="SEO, performance marketing, conversion optimization, web development and tracking use one shared data foundation. That keeps economic impact visible."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <PillarCard
              cta="Learn more ↗"
              pillar="SEO & AI SEO"
              discipline="SEO & AI SEO"
              description="Organic reach that works in Google as well as ChatGPT, Perplexity and Claude. Sustainable, compounding."
              href="/en/services/seo-ai-seo"
            />
            <PillarCard
              cta="Learn more ↗"
              pillar="Performance Marketing"
              discipline="Performance Marketing"
              description="Search, social media, and affiliate performance across Google Ads, Meta, TikTok, LinkedIn and Microsoft Ads — controlled, data-driven, no black-box promises."
              href="/en/services/performance-marketing"
            />
            <PillarCard
              cta="Learn more ↗"
              pillar="III — Conversion"
              discipline="CRO & Web"
              description="Conversion and web optimization as one connected craft. Hypothesis-driven, architecturally considered."
              href="/en/services/conversion-optimization"
            />
          </div>
        </Section>

        <Section
          number="04"
          kicker="Proprietary software"
          headline={<>We don't use <em>standard tooling.</em> We build the tools ourselves.</>}
          caption="Three proprietary tools are productively in use at Calvarius: Delta for feed management, picazzoo for AI creatives, GonzalAds for Meta campaign steering. Plus the ability to build custom SaaS components in a fraction of the previous time."
        >
          <RevealOnScroll>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/en/services/tools" className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90">
                Tools in detail →
              </Link>
              <Link to="/en/services/delta" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">Delta</Link>
              <span className="font-mono text-xs text-ink-4">·</span>
              <Link to="/en/services/picazzoo" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">picazzoo</Link>
              <span className="font-mono text-xs text-ink-4">·</span>
              <Link to="/en/services/gonzalads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">GonzalAds</Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          variant="deep"
          kicker="Proprietary tool"
          headline={<>Delta — <em>product data as a lever.</em></>}
          caption="In e-commerce the performance question is almost always a product-data question. DELTA is our feed-management layer, built exactly there — versioned, platform-specific, no external license fees."
        >
          <DeltaHero locale="en" />
        </Section>

        <LogoReferences locale="en" number="05" />

        <Section
          number="06"
          kicker="Proof"
          headline={<>Three numbers that <em>precisely describe</em> our daily work.</>}
          caption="No staged hero cases. Three figures that show what we stand for — verifiable through work with active clients."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {proofPoints.map((p) => (
                <div key={p.k} className="bg-white p-7 md:p-8">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                    {p.k}
                  </div>
                  <div className="mt-4 font-display text-2xl leading-snug text-cyan-deep md:text-3xl">
                    {p.v}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-ink-3">{p.b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="07"
          variant="tinted"
          kicker="How we think"
          headline={<>Four principles you can <em>hold us to.</em></>}
          caption="No values posters, no mission statements. Just operating rules that make a difference every day."
        >
          <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
            {principles.map((p) => (
              <div key={p.n} className="bg-white p-7">
                <div className="font-mono text-xs text-ink-4">{p.n}</div>
                <div className="mt-3 font-display text-xl text-ink">{p.t}</div>
                <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.b}</p>
              </div>
            ))}
          </div>
          <Callout>We don't earn more when you spend more — and that's by design.</Callout>
        </Section>

        <Section
          number="08"
          variant="deep"
          kicker="Engagement"
          headline={<>Let's talk about your <em>performance.</em></>}
          caption="If you want to know where your online marketing is currently leaving potential on the table – and how to change that in a structured way, we're happy to talk."
        >
          <CTABlock
            variant="deep"
            headline={<>On equal footing — without sales pressure.</>}
            body="Tell us briefly what it's about. We'll get back to you within two business days with a proposal for a first conversation, or an honest note if we're not the right fit."
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "See services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
